<template>
    <section class="mb-8">
        <h3
            class="text-base font-semibold text-slate-900 dark:text-slate-100 mb-3"
        >
            Invoices
        </h3>
        <!-- Invoice Table Header (hidden on mobile) -->
        <div
            class="hidden sm:flex items-center border-b border-slate-200 dark:border-slate-700 py-3"
        >
            <span
                class="text-sm font-medium text-slate-500 dark:text-slate-400 w-40"
            >
                Date
            </span>
            <span
                class="text-sm font-medium text-slate-500 dark:text-slate-400 flex-1"
            >
                Invoice #
            </span>
            <span
                class="text-sm font-medium text-slate-500 dark:text-slate-400 w-24"
            >
                Status
            </span>
            <span class="w-10"></span>
        </div>

        <!-- Invoice Rows -->
        <template v-if="invoices && invoices.length > 0">
            <div
                v-for="invoice in visibleInvoices"
                :key="invoice.id"
                class="flex flex-col sm:flex-row sm:items-center border-b border-slate-100 py-3 gap-1 sm:gap-0"
            >
                <span
                    class="text-sm text-slate-900 dark:text-slate-100 sm:w-40"
                >
                    <span
                        class="sm:hidden text-slate-500 dark:text-slate-400 font-medium"
                    >
                        Date:
                    </span>
                    {{ formatDate(invoice.created * 1000) }}
                </span>
                <span
                    class="text-sm text-slate-900 dark:text-slate-100 sm:flex-1"
                >
                    <span
                        class="sm:hidden text-slate-500 dark:text-slate-400 font-medium"
                    >
                        Invoice:
                    </span>
                    {{ invoice.number }}
                </span>
                <div
                    class="flex items-center justify-between sm:contents"
                >
                    <span class="sm:w-24">
                        <span
                            class="text-xs font-medium px-2 py-0.5 rounded-full"
                            :class="
                                invoiceStatusClass(
                                    invoice.status
                                )
                            "
                        >
                            {{
                                invoiceStatusLabel(
                                    invoice.status
                                )
                            }}
                        </span>
                    </span>
                    <span class="sm:w-10 text-right">
                        <a
                            v-if="invoice.invoice_pdf"
                            :href="invoice.invoice_pdf"
                            target="_blank"
                            class="text-slate-400 hover:text-slate-600 dark:text-slate-300"
                            title="Download invoice PDF"
                        >
                            <Icon
                                icon="download"
                                class="w-5 h-5"
                            />
                        </a>
                    </span>
                </div>
            </div>
            <button
                v-if="invoices.length > visibleInvoiceCount"
                class="text-sm font-medium text-slate-800 dark:text-slate-100 mt-3 hover:text-10x-orange transition-colors"
                @click="$emit('show-more')"
            >
                Show more
            </button>
        </template>

        <!-- Empty State -->
        <div
            v-else
            class="flex flex-col items-center justify-center py-8"
        >
            <Icon
                icon="note"
                :width="24"
                :height="24"
                class="text-slate-400 mb-4"
            />
            <p class="text-sm font-medium text-slate-900 dark:text-slate-100">
                No invoices yet
            </p>
            <p class="text-sm text-slate-500 dark:text-slate-400">
                Documents will appear here after payment
            </p>
        </div>
    </section>
</template>

<script setup>
import Icon from '@/components/ui/icon/icon.vue';
import dayjs from 'dayjs';

defineProps({
    invoices: {
        type: Array,
        default: () => [],
    },
    visibleInvoices: {
        type: Array,
        default: () => [],
    },
    visibleInvoiceCount: {
        type: Number,
        default: 4,
    },
    loadingSubscriptionPayments: {
        type: Boolean,
        default: false,
    },
});

defineEmits(['show-more']);

const formatDate = (timestamp) => {
    return dayjs(timestamp).format('MMM D, YYYY');
};

const invoiceStatusClass = (status) => {
    switch (status) {
        case 'paid':
            return 'bg-green-100 text-green-700';
        case 'open':
        case 'overdue':
            return 'bg-red-100 text-red-700';
        case 'refunded':
        case 'partially_refunded':
            return 'bg-amber-100 text-amber-700';
        case 'draft':
            return 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300';
        default:
            return 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300';
    }
};

const invoiceStatusLabel = (status) => {
    const labels = {
        paid: 'Paid',
        open: 'Open',
        overdue: 'Overdue',
        draft: 'Draft',
        void: 'Void',
        uncollectible: 'Uncollectible',
        refunded: 'Refunded',
        partially_refunded: 'Partial Refund',
    };
    return (
        labels[status] ||
        (status ? status.charAt(0).toUpperCase() + status.slice(1) : '')
    );
};
</script>
